import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import BusinessLogo from "../assets/images/Logo.png";
import useHttp from "../hooks/use-http";
import AnimateSpinner from "./AnimateSpinner";
import PaymentContext from "../store/PaymentContext";

const MENU_TO_CHANNEL = {
  "Card": "card",
  "Bank Transfer": "transfer",
  "Paytent QR": "qr",
  "Phone Number": "phone",
};

const maskEmail = (email) => {
  if (!email || typeof email !== "string" || !email.includes("@")) return email || "";
  const [localPart, domain] = email.split("@");
  if (!domain) return email;

  let maskedLocal;
  if (localPart.length <= 2) {
    maskedLocal = `${localPart[0]}***`;
  } else {
    maskedLocal = `${localPart.slice(0, 2)}****${localPart.slice(-1)}`;
  }

  const domainParts = domain.split(".");
  if (domainParts.length === 1) {
    return `${maskedLocal}@***`;
  }

  const domainName = domainParts[0];
  const tld = domainParts.slice(1).join(".");

  let maskedDomain;
  if (domainName.length <= 2) {
    maskedDomain = `${domainName[0]}***`;
  } else {
    maskedDomain = `${domainName[0]}***${domainName.slice(-1)}`;
  }

  return `${maskedLocal}@${maskedDomain}.${tld}`;
};

const MainNavigation = () => {
  const { sendRequest: MerchantInfo } = useHttp();
  const { sendRequest: PaymentMethod } = useHttp();
  const [merchantData, setMerchantData] = useState({});
  const [merchantId, setMerchantId] = useState();
  const [methods, setMethods] = useState([]);
  const [methodsReady, setMethodsReady] = useState(false);
  const { channel, setChannel } = useContext(PaymentContext);
  const { sendRequest: getTransactionStatus } = useHttp();
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  const { id } = useParams();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileLanding, setShowMobileLanding] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { sendRequest: fetchTransactionDetails } = useHttp();
  const [transactionInfo, setTransactionInfo] = useState({});

  const getTransactionInfo = (data) => {
    if (data?.success) {
      setTransactionInfo(data);
    }
  };

  const fetchTransactionInfo = useCallback(async () => {
    if (!id || !channel) return;
    fetchTransactionDetails(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/${id}?paymentMethod=${channel}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getTransactionInfo
    );
  }, [fetchTransactionDetails, id, channel]);

  useEffect(() => {
    fetchTransactionInfo();
  }, [fetchTransactionInfo]);

  const fetchTransactionStatus = useCallback(() => {
    getTransactionStatus(
      {
        url: `${process.env.REACT_APP_BASE_URL}api/Transaction/GetTransactionStatus/${id}`,
        headers: { "Content-Type": "application/json" },
      },
      (data) => {
        console.log(data, 'transaction data');
        const status = data?.data?.status;
        if (status) {
          const lowerStatus = status.toLowerCase();
          if (lowerStatus !== "initiated" && lowerStatus !== "processing") {
            setTransactionStatus(status);
            setShowStatusModal(true);
          }
        }
      }
    );
  }, [getTransactionStatus, id]);

  const hasFetchedStatus = useRef(false);

  useEffect(() => {
    if (id && !hasFetchedStatus.current) {
      hasFetchedStatus.current = true;
      fetchTransactionStatus();
    }
  }, [id, fetchTransactionStatus]);

  const navigate = useNavigate();
  const location = useLocation();

  const getMerchantInfo = useCallback((data) => {
    setMerchantData(data);
    setMerchantId(data?.data?.id);
  }, []);

  const getPaymentMethod = useCallback(
    (data) => {
      setMethods(data?.data || []);
      setMethodsReady(true);
    },
    [setMethods],
  );

  const fetchMerchantInfo = useCallback(async () => {
    MerchantInfo(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/merchant/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getMerchantInfo,
    );
  }, [MerchantInfo, getMerchantInfo, id]);

  const fetchPaymentMethod = useCallback(async () => {
    PaymentMethod(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payment-method/merchant?merchantId=${merchantId}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getPaymentMethod,
    );
  }, [PaymentMethod, getPaymentMethod, merchantId]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchMerchantInfo();
    };
    fetchData();
  }, [fetchMerchantInfo]);

  useEffect(() => {
    const fetchMethods = async () => {
      if (merchantId) {
        await fetchPaymentMethod();
      }
    };
    fetchMethods();
  }, [fetchPaymentMethod, merchantId]);

  const links = useMemo(
    () => [
      {
        to: `/${id}`,
        text: "Card",
        icons: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[20px] h-[20px]">
            <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="2" y1="10" x2="22" y2="10"></line>
            <line x1="6" y1="15" x2="10" y2="15"></line>
          </svg>
        ),
      },
      {
        to: `/phone/${id}`,
        text: "Phone Number",
        icons: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[20px] h-[20px]">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
            <line x1="12" y1="18" x2="12.01" y2="18"></line>
            <polyline points="9 10 12 7 15 10"></polyline>
            <line x1="12" y1="7" x2="12" y2="14"></line>
          </svg>
        ),
      },
      {
        to: `/transfer/${id}`,
        text: "Bank Transfer",
        icons: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[20px] h-[20px]">
            <path d="M3 21h18"></path>
            <path d="M19 21v-4"></path>
            <path d="M5 21v-4"></path>
            <path d="M2 10h20"></path>
            <path d="M12 2L2 10h20L12 2z"></path>
            <path d="M9 21v-4"></path>
            <path d="M15 21v-4"></path>
          </svg>
        ),
      },
      {
        to: `/qr/${id}`,
        text: "Paytent QR",
        icons: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-[20px] h-[20px]">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
            <path d="M7 17h1v-1h-1z"></path>
            <path d="M17 7h1V6h-1z"></path>
            <path d="M7 7h1V6h-1z"></path>
          </svg>
        ),
      },
    ],
    [id],
  );

  const normalizeMethodName = (method) => {
    const lower = method.toLowerCase().trim();
    if (lower === "pay with card" || lower === "card") return "Card";
    if (lower === "pay by transfer" || lower === "pay by bank transfer" || lower === "bank transfer") return "Bank Transfer";
    if (lower === "pay with phone number" || lower === "phone number") return "Phone Number";
    if (lower === "pay with paytentqr" || lower === "paytent qr" || lower === "paytentqr") return "Paytent QR";
    return method;
  };

  const methodTexts = methods
    .map((m) => normalizeMethodName(m.paymentMethod))
    .filter(Boolean);

  const filteredLinks = useMemo(() => {
    return methods.length === 0
      ? links
      : links.filter((link) => methodTexts.includes(link.text));
  }, [methods, links, methodTexts]);

  useEffect(() => {
    if (!id || id === "undefined") {
      navigate("/error");
      return;
    }
    if (
      !isMobile &&
      location.pathname === `/${id}` &&
      methods.length > 0 &&
      filteredLinks.length > 0 &&
      location.pathname !== filteredLinks[0].to
    ) {
      navigate(filteredLinks[0].to, { replace: true });
    }
  }, [location.pathname, navigate, id, methods, filteredLinks, isMobile]);

  const currentOptionName = useMemo(() => {
    if (location.pathname.startsWith(`/transfer/`)) return "Bank Transfer";
    if (location.pathname.startsWith(`/phone/`)) return "Phone Number";
    if (location.pathname.startsWith(`/qr/`)) return "Paytent QR";
    return "Card";
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith(`/transfer/`)) {
      setChannel("transfer");
    } else if (location.pathname.startsWith(`/phone/`)) {
      setChannel("phone");
    } else if (location.pathname.startsWith(`/qr/`)) {
      setChannel("qr");
    } else if (location.pathname === `/${id}` || location.pathname.startsWith(`/card/`)) {
      setChannel("card");
    } else if (filteredLinks.length > 0 && !channel) {
      const firstMenu = filteredLinks[0];
      const channelVal = MENU_TO_CHANNEL[firstMenu.text];
      setChannel(channelVal);
    }
  }, [location.pathname, id, setChannel, filteredLinks, channel]);

  const handleCloseOrGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
      setTimeout(() => {
        window.location.href = "https://mypayloft.com";
      }, 500);
    }
  };

  const { sendRequest, isLoading } = useHttp();

  const confirmCancel = (data) => {
    window.location.href = data?.data?.returnUrl;
  };

  const cancelTransaction = async () => {
    sendRequest(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/cancelTransaction/${id}`,
        headers: { "Content-Type": "application/json" },
      },
      confirmCancel
    );
  };

  if (!methodsReady || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <AnimateSpinner />
      </div>
    );
  }

  const isDefaultRoute = location.pathname === `/${id}` || location.pathname === `/card/${id}`;
  const isBaseRoute = location.pathname === `/${id}`;
  const showLanding = isMobile && isBaseRoute && showMobileLanding;

  const currentCurrency = transactionInfo?.data?.currency || "NGN";
  const formattedCurrencySymbol = currentCurrency === "NGN" ? "₦" : currentCurrency;
  const shoppingCartAmount = transactionInfo?.data?.amount || 0;
  const processingFeeAmount = transactionInfo?.data?.fee || 0;
  const totalAmount = shoppingCartAmount + processingFeeAmount;
  return (
    <div className="min-h-screen bg-[#5d7c8f] bg-blend-overlay flex items-center justify-center p-0 md:p-8 bg-cover bg-no-repeat bg-center bg-[url('../src/assets/images/background.png')] font-sans">
      {/* Centered Desktop Card Box */}
      <div className="w-full max-w-[780px] bg-white rounded-none shadow-none flex overflow-hidden min-h-[650px] border border-slate-200 hidden md:flex md:scale-[0.85] lg:scale-[0.9] origin-center">
        {/* Left Sidebar */}
        <div className="w-[220px] bg-[#f4faff] border-r border-slate-200 flex flex-col justify-between hidden md:flex">
          <div>
            {/* Payloft Logo */}
            <div className="flex items-center pl-6 border-b border-slate-200 bg-white md:bg-[#f4faff]">
              <img src={BusinessLogo} alt="Payloft" className="h-[100px] w-[128px]" />
            </div>
            {/* Select Payment Option Heading */}
            <div className="px-6 py-4 border-b border-slate-200/60 bg-[#f4faff]">
              <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">
                Select Payment Option
              </span>
            </div>
            {/* Menu Links */}
            <nav className="flex flex-col">
              {filteredLinks.map((link, index) => {
                const active = location.pathname === link.to || (link.to === `/${id}` && isDefaultRoute);
                return (
                  <NavLink
                    key={index}
                    to={link.to}
                    className={`flex items-center gap-4 px-6 py-14 border-b border-slate-200/60 font-bold transition-all duration-200 ${active
                      ? "bg-[#00aeef] text-white"
                      : "bg-[#f4faff] text-[#00aeef] hover:bg-[#e8f5fc]"
                      }`}
                    onClick={() => setChannel(MENU_TO_CHANNEL[link.text])}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">{link.icons}</span>
                    <span className={`${active ? "font-bold" : "font-light"} text-[16px] tracking-wide leading-none`}>{link.text}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Right Content Column */}
        <div className="flex-1 flex flex-col justify-between min-h-[650px] bg-white">
          {/* Header */}
          <div className="p-5 py-4 flex justify-between items-start border-b border-slate-200/60 bg-white">
            <div>
              <p className="text-[15px] font-medium text-slate-800 leading-none">Merchant</p>
              <h4 className="text-[13px] font-medium text-[#00aeef] uppercase mt-2 leading-none">
                {transactionInfo?.data?.merchantName || merchantData?.data?.name || ""}
              </h4>
              <p className="text-[11px] font-medium text-[#00aeef] mt-1 leading-none">
                {transactionInfo?.data?.email || ""}
              </p>
            </div>

            <div className="flex-1 flex justify-center px-4">
              <div>
                <p className="text-[15px] font-medium text-slate-800 leading-none mb-2">Order Summary</p>
                <div className="grid grid-cols-[100px_auto] gap-x-4 gap-y-0.5 text-xs">
                  <span className="text-slate-400 font-semibold leading-relaxed">Shopping Cart</span>
                  <span className="text-[#00aeef] font-medium text-right leading-relaxed">
                    {currentCurrency} {shoppingCartAmount.toLocaleString()}
                  </span>

                  <span className="text-slate-400 font-semibold leading-relaxed">Processing Fee</span>
                  <span className="text-[#00aeef] font-medium text-right leading-relaxed">
                    {currentCurrency} {processingFeeAmount.toLocaleString()}
                  </span>

                  <span className="text-slate-800 font-bold leading-relaxed">Total</span>
                  <span className="text-[#00aeef] font-bold text-right leading-relaxed">
                    {currentCurrency} {totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={cancelTransaction}
              className="text-slate-400 hover:text-slate-600 p-1 border border-slate-200 rounded-full active:bg-slate-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Child Routes Main Outlet */}
          <div className="flex-1 p-5 overflow-y-auto bg-white flex flex-col justify-between">
            <div className="flex-1">
              <Outlet context={{ transactionInfo, fetchTransactionInfo, merchantId }} />
            </div>
            {/* Powered by Badge */}
            <div className="mt-6 flex justify-center pb-1">
              <div className="px-4 py-2 border border-slate-200 rounded-lg bg-white flex items-center justify-center gap-2">
                <svg width="10" height="12" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect y="5" width="14" height="11" rx="2" fill="#f5a623" />
                  <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#f5a623" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="7" cy="10.5" r="1.5" fill="white" />
                </svg>
                <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest leading-none">
                  Powered by Payloft
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Screen View */}
      <div className="w-full min-h-screen bg-white flex flex-col md:hidden">
        {/* Top Header Bar */}
        <div className="h-[60px] border-b border-slate-200 flex items-center justify-between px-4 bg-white relative">
          <div className="w-10"></div>
          <span className="text-lg font-bold text-slate-800 absolute left-1/2 transform -translate-x-1/2">
            {showLanding ? "Payloft Checkout" : "Payment"}
          </span>
          <button
            onClick={cancelTransaction}
            className="text-slate-400 hover:text-slate-600 p-1 border border-slate-200 rounded-full active:bg-slate-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Merchant & Summary Details Section */}
        {!showLanding && (
          <div className="p-5 bg-slate-50 border-b border-slate-200/60">
            <div className="flex justify-between items-start mb-4">
              <img src={BusinessLogo} alt="Payloft" className="h-[60px] w-[90px] object-contain" />
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Order Summary</p>
                <div className="text-xs text-slate-400 font-semibold mt-1">
                  <span>Shopping Cart: </span>
                  <span className="font-medium text-[#00aeef]">
                    {formattedCurrencySymbol}{shoppingCartAmount.toLocaleString()}
                  </span>
                </div>
                <div className="text-xs text-slate-400 font-semibold">
                  <span>Processing Fee: </span>
                  <span className="font-medium text-[#00aeef]">
                    {formattedCurrencySymbol}{processingFeeAmount.toLocaleString()}
                  </span>
                </div>
                <div className="text-xs font-bold text-slate-800 mt-1">
                  <span>Total: </span>
                  <span className="text-sm font-bold text-[#00aeef]">
                    {formattedCurrencySymbol}{totalAmount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-[#00aeef] uppercase">
                {transactionInfo?.data?.merchantName || merchantData?.data?.name || ""}
              </h4>
              <p className="text-xs font-medium text-[#00aeef] mt-0.5">{transactionInfo?.data?.email || ""}</p>
            </div>
          </div>
        )}

        {/* Mobile Navigation Trigger Bar directly below header */}
        {!showLanding && (
          <div className="px-5 py-4 bg-slate-50 border-b border-slate-200/80">
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="w-full flex justify-between items-center px-4 py-3 bg-[#00aeef] hover:bg-[#00aeef]/95 active:bg-[#009dcf] transition-all duration-200 rounded-xl shadow-md shadow-[#00aeef]/10 text-left group"
            >
              <div className="flex items-center gap-2.5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
                <span className="text-[15px] font-extrabold text-white">Change Payment Option</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-white uppercase bg-white/20 px-2.5 py-1 rounded-lg border border-white/20">
                  {currentOptionName}
                </span>
                <svg className="w-4 h-4 text-white/80 group-hover:text-white transition-colors" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
                </svg>
              </div>
            </button>
          </div>
        )}

        {/* Form content Area */}
        <div className={`flex-1 overflow-y-auto flex flex-col justify-between bg-white ${showLanding ? "py-5" : "p-5"}`}>
          {showLanding ? (
            <div className="flex-1 flex flex-col justify-between">
              <div>
                {/* Logo and Message */}
                <div className="mb-6 px-5">
                  <img src={BusinessLogo} alt="Payloft" className="h-[55px] w-[90px] object-contain mb-3" />
                  <p className="text-slate-700 text-sm font-semibold mb-1">
                    Select one of the payment methods below
                  </p>
                  <p className="text-slate-400 text-xs font-medium">
                    {formattedCurrencySymbol}{totalAmount.toLocaleString()} to {transactionInfo?.data?.merchantName || merchantData?.data?.name || "PAYARENA"}
                  </p>
                </div>

                {/* Divider Line */}
                <div className="border-t border-dashed border-slate-200/80 my-4"></div>

                {/* Payment Methods List */}
                <div className="border-t border-b border-slate-200 divide-y divide-slate-200 mb-8 bg-white">
                  {filteredLinks.map((link, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setChannel(MENU_TO_CHANNEL[link.text]);
                        setShowMobileSidebar(false);
                        setShowMobileLanding(false);
                        navigate(link.to);
                      }}
                      className="w-full flex items-center justify-between py-5 px-6 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-[#1b2430] w-6 h-6 flex items-center justify-center flex-shrink-0">
                          {link.icons}
                        </span>
                        <span className="text-[17px] font-bold text-[#1b2430]">
                          {link.text}
                        </span>
                      </div>
                      <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bottom Cancel Button */}
              <div className="pb-4 px-5">
                <button
                  onClick={cancelTransaction}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-[#00aeef] hover:bg-[#00aeef]/95 active:bg-[#009dcf] text-white font-extrabold rounded-xl shadow-md shadow-[#00aeef]/10 transition-all duration-200"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                  <span>Cancel Payment</span>
                </button>
              </div>
            </div>
          ) : (
            <Outlet context={{ transactionInfo, fetchTransactionInfo }} />
          )}
        </div>

        {/* Mobile Footer */}
        <div className="p-5 flex flex-col items-center gap-3 bg-slate-50 border-t border-slate-100">
          <p className="text-[11px] text-slate-400 flex items-center gap-1 font-medium">
            <svg width="10" height="10" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="5" width="14" height="11" rx="2" fill="#00aeef" />
              <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#00aeef" strokeWidth="2" strokeLinecap="round" />
              <circle cx="7" cy="10.5" r="1.5" fill="white" />
            </svg> Your connection is encrypted and secure
          </p>
          <div className="px-5 py-2.5 border border-slate-200 rounded-lg bg-white flex items-center justify-center gap-2">
            <svg width="12" height="14" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="5" width="14" height="11" rx="2" fill="#f5a623" />
              <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#f5a623" strokeWidth="2" strokeLinecap="round" />
              <circle cx="7" cy="10.5" r="1.5" fill="white" />
            </svg>
            <span className="text-[10px] text-slate-500 font-extrabold uppercase tracking-widest leading-none">
              Powered by Payloft
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Left Sidebar Drawer */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 flex md:hidden animate-fade-in duration-200">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity duration-200"
            onClick={() => setShowMobileSidebar(false)}
          ></div>

          {/* Sidebar Panel Drawer */}
          <div className="relative flex flex-col w-[260px] max-w-xs bg-[#f4faff] h-full shadow-2xl border-r border-slate-200 animate-slide-right animate-in slide-in-from-left duration-250 z-50">
            {/* Close Button on Sidebar */}
            <div className="flex justify-between items-center p-5 border-b border-slate-200 bg-white">
              <img src={BusinessLogo} alt="Payloft" className="h-[40px] w-[80px] object-contain" />
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="p-1.5 border border-slate-200 rounded-full text-slate-400 hover:text-slate-600 bg-slate-50 active:bg-slate-100 flex items-center justify-center w-7 h-7"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Menu Links */}
            <nav className="flex flex-col flex-1 bg-[#f4faff]">
              {filteredLinks.map((link, index) => {
                const active = location.pathname === link.to || (link.to === `/${id}` && isDefaultRoute);
                return (
                  <NavLink
                    key={index}
                    to={link.to}
                    className={`flex items-center gap-4 px-6 py-6 border-b border-slate-200/60 font-bold transition-all duration-200 ${active
                      ? "bg-[#00aeef] text-white"
                      : "bg-[#f4faff] text-[#00aeef] hover:bg-[#e8f5fc]"
                      }`}
                    onClick={() => {
                      setChannel(MENU_TO_CHANNEL[link.text]);
                      setShowMobileSidebar(false);
                      setShowMobileLanding(false);
                    }}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">{link.icons}</span>
                    <span className={`${active ? "font-bold" : "font-light"} text-[16px] tracking-wide leading-none`}>
                      {link.text}
                    </span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Bottom Powered By lock badge */}
            <div className="p-5 border-t border-slate-200/60 bg-white flex justify-center">
              <div className="px-4 py-2 border border-slate-200 rounded-lg bg-white flex items-center justify-center gap-2">
                <svg width="10" height="12" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect y="5" width="14" height="11" rx="2" fill="#f5a623" />
                  <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#f5a623" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="7" cy="10.5" r="1.5" fill="white" />
                </svg>
                <span className="text-[9px] text-slate-500 font-extrabold uppercase tracking-widest leading-none">
                  Powered by Payloft
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning/Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center p-4 animate-fade-in">
          <div className="w-[440px] bg-white p-8 rounded-[20px] shadow-2xl relative border border-slate-100 transform transition-all duration-300 scale-100">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center mb-6">
                <span className="text-amber-500 text-3xl font-bold font-sans">!</span>
              </div>
              <h3 className="text-2xl text-lightBlack font-extrabold mb-3">
                Transaction Status
              </h3>
              <p className="text-normalText text-base font-normal leading-relaxed mb-8">
                This transaction status is <span className="font-semibold text-primary">{transactionStatus}</span> and cannot be processed again.
              </p>
              <button
                onClick={handleCloseOrGoBack}
                className="w-full h-[54px] rounded-xl font-bold bg-primary text-white hover:bg-opacity-90 active:scale-95 transition-all shadow-lg shadow-primary/20"
              >
                {window.history.length > 1 ? "Go Back" : "Close Page"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainNavigation;
